import React, { useEffect, useState, useMemo } from 'react';
import { inject, observer, Provider } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import * as routes from 'routes';

import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

import Layout from 'components/Layout';
import { formatAmountValue } from 'helpers/helpers';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import { LocationTabs, TabData } from 'components/LocationTabs/LocationTabs';
import NewAccountButton from 'components/NewAccountButton';

import { RootStore } from 'stores/rootStore';
import { AccountsStore } from 'stores/accountsStore';
import Drafts from './Drafts';
import ActiveAccounts from './ActiveAccounts';
import ImportantDates from './ImportantDates';
import Inactive from './Inactive';
import useStyles from 'pages/Accounts/Detail/styles';
import { EdwardColors, Currency } from 'enums';
import { ViewKeys } from 'stores/userStoreKC';

//import { AdvisorMenuItems } from 'components/AdvisorMenu/AdvisorMenu';

interface Props {
  rootStore: RootStore;
}
export enum Widths {
  MOBILE = 0,
  TABLET = 1,
  MID = 2,
  LAPTOP = 3,
  DESKTOP = 4,
}

const Accounts: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new AccountsStore(rootStore));
  const { fetchSummary, fetchDrafts, fetchActives, fetchDates, fetchInactive, summary } = store;
  const classes = useStyles({});

  useEffect(() => {
    fetchSummary();
    fetchActives();
    fetchDates();
    fetchDrafts();
    fetchInactive();
  }, [fetchSummary, fetchDrafts, fetchActives, fetchDates, fetchInactive]);

  const tabs: TabData[] = useMemo(
    () => [
      { translation: 'accounts_active', url: routes.ACCOUNTS_ACTIVE },
      {
        translation: 'accounts_importantDates',
        url: routes.ACCOUNTS_IMPORTANTDATES,
        icon: store.questionnaireOverdue ? <NotificationImportantIcon htmlColor={EdwardColors.ALERT} /> : undefined,
        tooltip: store.questionnaireOverdue ? t('accounts_questionnaireOverdue') : undefined,
      },
      { translation: 'accounts_drafts', url: routes.ACCOUNTS_DRAFTS },
      { translation: 'accounts_inactive', url: routes.ACCOUNTS_INACTIVE },
    ],
    [t, store.questionnaireOverdue],
  );

  const [tab, setTab] = useState(0);

  const handleTabChange = (value: number) => {
    setTab(value);
  };

  return (
    <Layout title={t('pageTitle_accounts')}>
      {/*menuItem={AdvisorMenuItems.ACCOUNTS}*/}

      {!rootStore.stores.userStore.view[ViewKeys.DEMO_MODE] && <Grid container alignItems="center" spacing={0} className={classes.summary}>
        <Grid item container xs={12} sm={3} spacing={0} className={classes.infoBox}>
          <Grid item xs={12} className={classes.label}>
            {`${t('accounts_overview_totalAum')}: `}
          </Grid>
          <Grid className={`${classes.bigValue}`}>
            {summary ? formatAmountValue(summary.totalAum.value, Currency.CZK) : <LoadingIndicator size={24} />}
          </Grid>
        </Grid>
        <Grid item container xs={12} sm={3} spacing={0} className={classes.infoBox}>
          <Grid item xs={12} className={classes.label}>
            {`${t('accounts_overview_totalAccounts')}: `}
          </Grid>
          <Grid className={`${classes.bigValue}`}>
            {summary ? summary.totalAccounts : <LoadingIndicator size={24} />}
          </Grid>
        </Grid>
        <Grid item container xs={12} sm={3} spacing={0} className={classes.infoBox}>
          <Grid item xs={12} className={classes.label}>
            {`${t('accounts_overview_averageAum')}: `}
          </Grid>
          <Grid className={`${classes.bigValue}`}>
            {summary ? formatAmountValue(summary.averageAum.value, Currency.CZK) : <LoadingIndicator size={24} />}
          </Grid>
        </Grid>
        <Grid item container xs={12} sm={3} spacing={0} className={classes.infoBox}>
          <Grid item xs={12} className={classes.label}>
            {`${t('accounts_overview_totalRegularInvestment')}: `}
          </Grid>
          <Grid className={`${classes.bigValue}`}>
            {summary ? formatAmountValue(summary.totalPeriodic.value, Currency.CZK) : <LoadingIndicator size={24} />}
          </Grid>
        </Grid>
      </Grid>}

      <Paper className={classes.tabs} elevation={0}>
        <Grid container alignItems="stretch" spacing={0}>
          <Grid item xs={12} sm={12} md={8}>
            <LocationTabs value={tab} onChange={handleTabChange} tabs={tabs} noPadding overflow fullWidth />
          </Grid>
          <Grid item xs={12} sm={12} md={4} className={classes.newButtonContainer}>
            <NewAccountButton />
          </Grid>
        </Grid>

        <Provider newAccountsStore={store}>
          <div>
            {tab === 0 && <ActiveAccounts />}
            {tab === 1 && <ImportantDates />}
            {tab === 2 && <Drafts />}
            {tab === 3 && <Inactive />}
          </div>
        </Provider>
      </Paper>
    </Layout>
  );
};

export default inject('rootStore')(observer(Accounts));
